import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConflictResolutionDialog } from '@/components/settings/ConflictResolutionDialog';
import { HealthCheckDialog } from '@/components/settings/HealthCheckDialog';
import type { HealthCheckResult, HealthCheckFinding } from '@/types/healthCheck';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

interface SettingsPageDialogsProps {
  showCancelDialog: boolean;
  setShowCancelDialog: (open: boolean) => void;
  onConfirmCancel: () => void;
  showConflictDialog: boolean;
  conflictServerSettings: OrganisationTimesheetSettings | null;
  fullSettings: OrganisationTimesheetSettings | null;
  onConflictResolve: (action: 'discard' | 'override') => void;
  onConflictCancel: () => void;
  showHealthCheckDialog: boolean;
  healthCheckResult: HealthCheckResult | null;
  onHealthCheckClose: () => void;
  onApplyHealthCheckFix: (finding: HealthCheckFinding) => void;
}

export const SettingsPageDialogs: React.FC<SettingsPageDialogsProps> = ({
  showCancelDialog,
  setShowCancelDialog,
  onConfirmCancel,
  showConflictDialog,
  conflictServerSettings,
  fullSettings,
  onConflictResolve,
  onConflictCancel,
  showHealthCheckDialog,
  healthCheckResult,
  onHealthCheckClose,
  onApplyHealthCheckFix,
}) => (
  <>
    {/* Cancel Confirmation Dialog */}
    <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Are you sure you want to discard them? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirmCancel}>
            Discard changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Conflict Resolution Dialog */}
    {showConflictDialog && conflictServerSettings && fullSettings && (
      <ConflictResolutionDialog
        open={showConflictDialog}
        currentSettings={fullSettings}
        serverSettings={conflictServerSettings}
        onResolve={onConflictResolve}
        onCancel={onConflictCancel}
      />
    )}

    {/* Health Check Dialog */}
    {showHealthCheckDialog && healthCheckResult && (
      <HealthCheckDialog
        result={healthCheckResult}
        onClose={onHealthCheckClose}
        onApplyFix={onApplyHealthCheckFix}
      />
    )}
  </>
);
